<template>
  <div class="">
    <div v-if="isViewable">
      <ts-form-section title="Topic" class="form-section">
        <ts-form-item label="Product / Service description">
          <p class="text">
            {{ brief.topic.description }}
          </p>
        </ts-form-item>
        <div v-if="brief.topic.supportingFiles.length" class="files">
          <div v-for="file in brief.topic.supportingFiles" :key="file.id" class="file">
            <p class="filename">
              {{ file.name }}
            </p>
            <ts-button :disabled="isPending(file.status)" size="mini" circle @click="handleFileDownloadClick(file)">
              <SaveMini />
            </ts-button>
          </div>
        </div>
      </ts-form-section>
      <ts-form-section
        title="Campaign objectives / KPI"
        legend="How will you measure campaign success?"
        class="form-section"
      >
        <ts-form-item label="Primary objective" class="form-item">
          <p class="text">
            {{ formatObjective(brief.objectives.primaryObjective) }}
          </p>
        </ts-form-item>
        <ts-form-item label="Secondary objective" class="form-item">
          <p class="text">
            {{ formatObjective(brief.objectives.secondaryObjective) }}
          </p>
        </ts-form-item>
        <div v-if="brief.objectives.supportingFiles.length" class="files">
          <div v-for="file in brief.objectives.supportingFiles" :key="file.id" class="file">
            <p class="filename">
              {{ file.name }}
            </p>
            <ts-button size="mini" :disabled="isPending(file.status)" circle @click="handleFileDownloadClick(file)">
              <SaveMini />
            </ts-button>
          </div>
        </div>
      </ts-form-section>
      <ts-form-section title="Audience &amp; messaging" class="form-section">
        <div v-for="(audience, i) in brief.audiences" :key="i">
          <ts-collapse>
            <template #title>#{{ i + 1 }} - {{ audience.description }}</template>
            <p class="item-group-title">About this target</p>
            <ts-form-item label="What problem do you uniquely solve for them with your product?" class="form-item">
              <p class="text">
                {{ audience.problemSolved }}
              </p>
            </ts-form-item>
            <ts-form-item label="What has worked successfully with them in the past or not?" class="form-item">
              <p class="text">
                {{ audience.previousResults }}
              </p>
            </ts-form-item>
            <p class="item-group-title">Messaging this target</p>
            <ts-form-item
              label="What are the key messages you want to convey? (please rank them by priority order)"
              class="form-item"
            >
              <p class="text">
                {{ audience.keyMessages }}
              </p>
            </ts-form-item>
            <ts-form-item label="If you could make people recall only one thing, what would it be?" class="form-item">
              <p class="text">
                {{ audience.thingToRecall }}
              </p>
            </ts-form-item>
            <ts-form-item label="Proof" class="form-item">
              <p class="text">
                {{ audience.proof }}
              </p>
            </ts-form-item>
            <ts-form-item label="Call to action" class="form-item">
              <p class="text">
                {{ audience.callToAction }}
              </p>
            </ts-form-item>
            <div v-if="audience.supportingFiles.length" class="files">
              <div v-for="file in audience.supportingFiles" :key="file.id" class="file">
                <p class="filename">
                  {{ file.name }}
                </p>
                <ts-button size="mini" :disabled="isPending(file.status)" circle @click="handleFileDownloadClick(file)">
                  <SaveMini />
                </ts-button>
              </div>
            </div>
          </ts-collapse>
        </div>
      </ts-form-section>
      <ts-form-section title="Scope of work" class="form-section">
        <div class="horizontal-items">
          <ts-form-item label="Deliverables">
            <p class="text">
              {{ brief.scope.deliverables }}
            </p>
          </ts-form-item>
          <ts-form-item label="Languages needed">
            <p class="text">
              {{ brief.scope.languagesNeeded }}
            </p>
          </ts-form-item>
        </div>
        <div v-if="brief.scope.supportingFiles.length" class="files">
          <div v-for="file in brief.scope.supportingFiles" :key="file.id" class="file">
            <p class="filename">
              {{ file.name }}
            </p>
            <ts-button size="mini" :disabled="isPending(file.status)" circle @click="handleFileDownloadClick(file)">
              <SaveMini />
            </ts-button>
          </div>
        </div>
      </ts-form-section>
      <ts-form-section
        v-for="(additionalSection, i) in brief.additionalSections"
        :key="i"
        :title="additionalSection.name"
        class="form-section"
      >
        <ts-form-item v-for="(field, j) in additionalSection.fields" :key="j" :label="field.name" class="form-item">
          <p class="text">
            {{ field.value }}
          </p>
        </ts-form-item>
      </ts-form-section>
      <ts-form-section title="Brief" legend="Complete your brief with files" class="form-section">
        <div v-if="brief.supportingFiles.length" class="files">
          <div v-for="file in brief.supportingFiles" :key="file.id" class="file">
            <p class="filename">
              {{ file.name }}
            </p>
            <ts-button size="mini" :disabled="isPending(file.status)" circle @click="handleFileDownloadClick(file)">
              <SaveMini />
            </ts-button>
          </div>
        </div>
      </ts-form-section>
    </div>
    <div class="flex items-center justify-center h-full" v-if="!isViewable">
      <div class="">
        <div class="flex items-center justify-center mb-4">
          <visualize></visualize>
        </div>
        <template v-if="isCreator">
          <h1 class="title text-center mb-2">This step is not available for you !!</h1>
        </template>
        <template v-else>
          <h1 class="title text-center mb-2">This step is currently empty !</h1>
          <p class="subtitle text-center text-gray-500">please edit the brief to continue</p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import projectViewHelpers from '@/mixins/projectViewHelpers';
import Visualize from '@/components/the-source-ui/icons/Visualize.vue';
import SaveMini from '@/assets/save-mini.svg';
export default {
  name: 'BriefItem',
  mixins: [projectViewHelpers],
  components: {
    SaveMini,
    Visualize
  },
  props: {
    brief: {
      type: Object,
      required: true
    }
  },
  computed: {
    isViewable() {
      return this.brief && this.hasContent();
    }
  },
  methods: {
    forceFileDownload(file, response) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
    },
    hasContent() {
      return this.brief.validated || !this.isEmpty(this.brief, ['updatedAt', 'updatedBy', 'id']);
    },
    isEmpty(data, except = []) {
      if (!data) {
        return true;
      }

      if (typeof data === 'string') {
        return data.length == 0;
      }

      if (Array.isArray(data)) {
        return data.length == 0;
      }

      if (typeof data === 'object') {
        for (let key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key) && !except.includes(key)) {
            let value = data[key];
            if (!this.isEmpty(value)) return false;
          }
        }
      }

      return true;
    },
    handleEditClick() {
      this.$router.push({ name: 'edit-brief' });
    },
    handleFileDownloadClick(file) {
      axios
        .get(file.url, {
          responseType: 'arraybuffer'
        })
        .then(response => {
          this.forceFileDownload(file, response);
        });
    },
    handleValidate() {
      this.updateCurrentProjectBriefValidated({ value: true });
    }
  }
};
</script>

<style lang="scss" scoped>
.flex {
  display: flex;
}
.text-gray-500 {
  color: #9aa5b7;
}
.h-full {
  height: 100%;
}
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.ml-4 {
  margin-left: 1rem;
}
.mb-4 {
  margin-bottom: 1rem;
}
.header {
  display: flex;
  margin-bottom: 30px;
}

.title {
  flex-grow: 1;

  font-family: Roboto;
  font-size: 20px;
  font-weight: 500;
  color: #6d7587;
}

.last-update {
  font-family: Roboto;
  font-size: 12px;
  font-style: italic;
  color: #6d7587;

  margin-right: 10px;
}

.edition {
  display: flex;
  align-items: center;
}

.form-section {
  margin-right: 160px;
  margin-bottom: 75px;
}

.form-item {
  margin: 12px 0;
}

.item-group-title {
  font-family: Roboto;
  font-size: 16px;
  font-weight: 500;
  color: #000000;
}

.horizontal-items {
  display: flex;

  > * {
    margin-right: 20px;
  }
}

.text {
  font-family: Roboto;
  font-size: 16px;
  color: #000000;
  overflow-wrap: anywhere;
}

.files {
  margin-top: 16px;
}

.file {
  background: #ebecf0;
  background-image: linear-gradient(-42deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0.4) 100%);
  background-blend-mode: soft-light, normal;
  background-clip: padding-box;
  border: 2px solid rgba(255, 255, 255, 0.32);
  border-radius: 20px;
  font-size: 12px;
  padding: 6px 12px;
  margin: 6px 0;
  box-shadow: -5px -5px 10px 0 rgba(250, 251, 255, 0.8), 5px 5px 10px 0 rgba(166, 171, 189, 0.48);

  font-family: Roboto;
  font-size: 16px;
  font-weight: 500;
  color: #000000;

  display: flex;

  .filename {
    flex-grow: 1;
  }
}

.actions {
  width: 100%;
  margin-top: 100px;
  display: flex;
  justify-content: flex-end;
}
</style>